import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from 'src/lib/utils';

const borderVariants = cva('absolute top-0 left-0 right-0 h-[3px] rounded-t-xl', {
  variants: {
    accent: {
      default: 'bg-gradient-to-r from-primary to-primary/80',
      amber: 'bg-gradient-to-r from-secondary to-secondary/90',
      danger: 'bg-gradient-to-r from-destructive to-destructive/90',
      success: 'bg-gradient-to-r from-success to-success/90',
    },
  },
  defaultVariants: {
    accent: 'default',
  },
});

const MetricCard = React.forwardRef(function MetricCard({ className, accent, children, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn('relative bg-card rounded-xl border shadow-sm p-6 overflow-hidden', className)}
      {...props}>
      <div className={borderVariants({ accent })} />
      {children}
    </div>
  );
});

const MetricLabel = React.forwardRef(function MetricLabel({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn('font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2.5', className)}
      {...props}
    />
  );
});

const MetricValue = React.forwardRef(function MetricValue({ className, unit, children, ...props }, ref) {
  return (
    <div ref={ref} className={cn('font-display text-[40px] leading-none mb-1.5', className)} {...props}>
      {children}
      {unit && <span className='text-lg text-muted-foreground font-body font-normal ml-1'>{unit}</span>}
    </div>
  );
});

const MetricDelta = React.forwardRef(function MetricDelta({ className, direction, children, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'text-xs flex items-center gap-1.5',
        direction === 'up' && 'text-danger',
        direction === 'down' && 'text-success',
        className,
      )}
      {...props}>
      {direction === 'up' && <span>&#9650;</span>}
      {direction === 'down' && <span>&#9660;</span>}
      {children}
    </div>
  );
});

const MetricSparkline = React.forwardRef(function MetricSparkline({ className, children, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'mt-4 h-9 bg-gradient-to-b from-primary/15 to-primary/[0.03] rounded relative overflow-hidden',
        className,
      )}
      {...props}>
      <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-primary/70 to-transparent' />
      {children}
    </div>
  );
});

export { MetricCard, MetricLabel, MetricValue, MetricDelta, MetricSparkline };
